import Observer from '@capsule/observer'

// FIXME: remove when add to Capsule
declare global {
	interface Window {
		generatePageHitID: () => string
	}
}

export default class CompanionSlideBatch {
	private contentContainer: HTMLElement | null
	private observer?: MutationObserver
	private initializedSlides: Set<Element>

	constructor() {
		this.contentContainer = document.getElementById('companionSlide-contentTarget')
		this.initializedSlides = new Set()
	}

	init() {
		if (!this.contentContainer) return

		// Initialize existing slides
		const slides = document.querySelectorAll('.companionSlideBatch-slide')
		this.initSlides(Array.from(slides))
		this.observeNewSlides()
	}

	/** Watches for new slides and initializes them */
	private observeNewSlides() {
		if (!this.contentContainer) return

		this.observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type === 'childList') {
					const newSlides = this.getNewSlides()
					if (newSlides.length > 0) {
						this.initSlides(newSlides)
					}
				}
			}
		})

		// Observe only child additions
		this.observer.observe(this.contentContainer, {
			childList: true,
			subtree: true
		})
	}

	/** Returns uninitialized slides */
	private getNewSlides(): Element[] {
		// TODO: check perf
		const allSlides = document.querySelectorAll('.companionSlideBatch-slide')
		return Array.from(allSlides).filter((slide) => !this.initializedSlides.has(slide))
	}

	/**
	 * Initializes a group of slides
	 * @param slides
	 */
	private initSlides(slides: Element[]) {
		const slidesImages = []

		for (const slide of slides) {
			this.initSlide(slide)
			this.initializedSlides.add(slide)

			const slideImage = slide.querySelector(
				'.companionSlideBatch-slideImage img:not([data-diapo-item-number="1"])'
			)

			if (slideImage) {
				slidesImages.push(
					slide.querySelector(
						'.companionSlideBatch-slideImage img:not([data-diapo-item-number="1"])'
					)
				)
			}
		}

		// Observe datalayer push
		const observer = new Observer({
			elements: slidesImages,
			rootMargin: '0px 0px 0px 0px',
			onIntersection: (entry) => {
				const newPageHitId = window.generatePageHitID()
				const photoTitle = entry.target.getAttribute('data-diapo-item-title')
				const photoIndex = entry.target.getAttribute('data-diapo-item-number')

				window.dataLayer.push({
					event: 'page_view_sp',
					diapo_item_number: `#photo-${photoIndex}`,
					diapo_view: true,
					photo_title: photoTitle ?? '',
					page_hit_id: newPageHitId
				})

				// @ts-expect-error
				window.dataLayerWysistat.push({
					event: 'page_view_sp'
				})
			},
			threshold: 0.5
		})

		observer.observe()
	}

	/**
	 * Sets up content truncation and buttons for a slide
	 * @param slide
	 */
	private initSlide(slide: Element) {
		const content = slide.querySelector('.companionSlideBatch-slideBody')
		const showMoreButton = slide.querySelector('#showMore')
		const showLessButton = slide.querySelector('#showLess')

		if (!content || !showMoreButton || !showLessButton) return

		const isTruncated = content.scrollHeight > content.clientHeight

		if (isTruncated) {
			showMoreButton.classList.remove('hidden')
			this.addEventOnButtonsShowMoreAndShowLess(content, showMoreButton, showLessButton)
		}
	}

	/** Adds click handlers to show/hide buttons */
	private addEventOnButtonsShowMoreAndShowLess(
		content: Element,
		showMoreButton: Element,
		showLessButton: Element
	) {
		showMoreButton.addEventListener('click', () => {
			this.toggleContent(content, showMoreButton, showLessButton)
		})
		showLessButton.addEventListener('click', () => {
			this.toggleContent(content, showMoreButton, showLessButton)
		})
	}

	/**
	 * Toggles content visibility and button states
	 * @param content
	 * @param showMoreButton
	 * @param showLessButton
	 */
	private toggleContent(content: Element, showMoreButton: Element, showLessButton: Element) {
		content.classList.toggle('isExpanded')
		showMoreButton.classList.toggle('hidden')
		showLessButton.classList.toggle('hidden')
	}
}
